/**
 * Pre-computed monthly rollups (FR-DASH-01 to FR-DASH-08).
 *
 * "A hard requirement, not an optimisation." The dashboard reads a handful of these and
 * never thousands of transactions, so its cost stays flat whether the book holds 637
 * records or 60,000 (FR-NFR-03).
 *
 * Two paths must produce byte-identical results, and that equivalence is the whole
 * safety story for AD-02:
 *
 *   · `rollupDeltas` — incremental, applied by `increment()` in the same batch as the
 *     transaction. Works offline and merges commutatively.
 *   · `recomputeRollup` — from scratch, over a month's transactions. Used by the manual
 *     rebuild (FR-DASH-06) and the nightly verification.
 *
 * If they ever disagree, the rollup is wrong and the transactions are right. Rollups are
 * a cache; transactions are the record.
 */

import type { AccountType } from './accounts';
import type { MonthKey } from './dates';
import type { Paise } from './money';
import { balanceEffects, classifyOutflow, type Transaction } from './transactions';

export interface AccountFlow {
  inPaise: Paise;
  outPaise: Paise;
}

export interface Rollup {
  monthKey: MonthKey;
  incomePaise: Paise;
  expensePaise: Paise;
  savingsPaise: Paise;
  totalOutflowPaise: Paise;
  transferPaise: Paise;
  lendingPaise: Paise;
  byCategory: Record<string, Paise>;
  byAccount: Record<string, AccountFlow>;
  byBusiness: { business: Paise; personal: Paise };
  txnCount: number;
}

export function emptyRollup(monthKey: MonthKey): Rollup {
  return {
    monthKey,
    incomePaise: 0,
    expensePaise: 0,
    savingsPaise: 0,
    totalOutflowPaise: 0,
    transferPaise: 0,
    lendingPaise: 0,
    byCategory: {},
    byAccount: {},
    byBusiness: { business: 0, personal: 0 },
    txnCount: 0,
  };
}

export type AccountTypeLookup = (accountId: string) => AccountType | undefined;

/** Category bucket for a transaction with no category — the 180 "Expenses" rows land here. */
export const UNCATEGORISED = 'uncategorised';

/**
 * The field deltas one transaction contributes to its month's rollup.
 *
 * Keys are Firestore dot-paths, so the caller can hand them straight to `increment()`.
 * `sign` of -1 produces the exact reverse, which is what makes an edit or a delete a
 * matter of subtracting the old and adding the new.
 */
export function rollupDeltas(
  txn: Transaction,
  accountType: AccountTypeLookup,
  sign: 1 | -1 = 1,
): Record<string, number> {
  const fields: Record<string, number> = {};
  const add = (path: string, value: number) => {
    if (value === 0) return;
    fields[path] = (fields[path] ?? 0) + value * sign;
  };

  const amount = txn.amountInrPaise;
  const kind = classifyOutflow(txn, accountType);

  if (txn.type === 'income') {
    add('incomePaise', amount);
  } else if (kind === 'expense') {
    add('expensePaise', amount);
    add('totalOutflowPaise', amount);
    add(`byCategory.${txn.categoryId ?? UNCATEGORISED}`, amount);
    add(`byBusiness.${txn.isBusiness ? 'business' : 'personal'}`, amount);
  } else if (kind === 'savings') {
    add('savingsPaise', amount);
    add('totalOutflowPaise', amount);
  } else if (kind === 'lending') {
    // Excluded from every headline figure, but tracked so the dashboard can explain
    // where the money went rather than leaving it unaccounted for.
    add('lendingPaise', amount);
  } else if (txn.type === 'transfer') {
    // A loan EMI is two things in one record (FR-LOAN-06): the interest is spending, the
    // principal is a move between the user's own accounts. Splitting it here rather than
    // writing two documents follows FR-TXN-04's reasoning — one record cannot fall out of
    // step with itself — and the two parts still sum to the amount, which is what keeps
    // `expenses + savings + transfers + lending` equal to the ledger.
    const interest = interestInrPaise(txn);
    if (interest > 0) {
      add('expensePaise', interest);
      add('totalOutflowPaise', interest);
      add(`byCategory.${txn.categoryId ?? UNCATEGORISED}`, interest);
      add(`byBusiness.${txn.isBusiness ? 'business' : 'personal'}`, interest);
    }
    add('transferPaise', amount - interest);
  }

  // Per-account flow, from the same effects that move the balances — so a rollup can
  // never disagree with an account about which way money went.
  for (const effect of balanceEffects(txn)) {
    const side = effect.deltaPaise >= 0 ? 'inPaise' : 'outPaise';
    add(`byAccount.${effect.accountId}.${side}`, Math.abs(effect.deltaPaise));
  }

  add('txnCount', 1);
  return fields;
}

/**
 * Turn dot-paths into the nested object Firestore actually needs.
 *
 * **A dot in a `setDoc(…, { merge: true })` key is a literal character, not a path.**
 * Only `updateDoc` and `FieldPath` treat dots as paths — and `updateDoc` fails outright
 * on a missing document, which is no good here because the first transaction of a month
 * creates its rollup.
 *
 * Writing `{ 'byCategory.food': increment(n) }` therefore produces a top-level field
 * *named* `byCategory.food`, sitting beside the `byCategory` map rather than inside it —
 * so the increments land somewhere nothing reads, the scalar totals still look perfect,
 * and the dashboard's category panel is quietly always empty. That is exactly what
 * happened, and it survived until a rollup was compared field-by-field against a
 * recomputed one. Nesting the payload is the fix.
 *
 * `wrap` keeps this pure: the caller passes Firestore's `increment`.
 */
export function nestFields<T>(
  fields: Record<string, number>,
  wrap: (value: number) => T,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};

  for (const [path, value] of Object.entries(fields)) {
    const parts = path.split('.');
    let node = payload;
    for (const part of parts.slice(0, -1)) {
      const existing = node[part];
      const child = (existing ?? {}) as Record<string, unknown>;
      node[part] = child;
      node = child;
    }
    node[parts[parts.length - 1]] = wrap(value);
  }

  return payload;
}

/**
 * The interest portion of a transfer, in INR.
 *
 * `interestPaise` is stated in the transaction's own currency, while every rollup figure
 * is INR (SD-05). Converted with the same frozen rate the amount used, so a foreign-
 * currency EMI could not put the split and the total on different scales.
 */
function interestInrPaise(txn: Transaction): Paise {
  const interest = txn.interestPaise ?? 0;
  if (interest === 0 || txn.currency === 'INR') return interest;
  return Math.round((interest * txn.ratePpm) / 1_000_000);
}

/** Deltas for an edit, keyed by month — an edit can move a transaction across months. */
export function editDeltas(
  before: Transaction,
  after: Transaction,
  accountType: AccountTypeLookup,
): Map<MonthKey, Record<string, number>> {
  const byMonth = new Map<MonthKey, Record<string, number>>();
  const merge = (monthKey: MonthKey, fields: Record<string, number>) => {
    const current = byMonth.get(monthKey) ?? {};
    for (const [path, value] of Object.entries(fields)) {
      current[path] = (current[path] ?? 0) + value;
    }
    byMonth.set(monthKey, current);
  };

  merge(before.monthKey, rollupDeltas(before, accountType, -1));
  merge(after.monthKey, rollupDeltas(after, accountType, 1));

  // Drop paths that cancelled out, so an edit that changed nothing relevant writes
  // nothing — and a same-month edit does not touch fields it did not affect.
  for (const [monthKey, fields] of byMonth) {
    for (const [path, value] of Object.entries(fields)) {
      if (value === 0) delete fields[path];
    }
    if (Object.keys(fields).length === 0) byMonth.delete(monthKey);
  }

  return byMonth;
}

/**
 * Build a month's rollup from its transactions.
 *
 * The authority when the incremental path is in doubt. Deliberately implemented by
 * applying `rollupDeltas` rather than by a second summation: two independent
 * implementations agreeing proves only that both were written by the same person on the
 * same day. This way, the test that they agree is a real test of the delta logic.
 */
export function recomputeRollup(
  monthKey: MonthKey,
  transactions: Transaction[],
  accountType: AccountTypeLookup,
): Rollup {
  const rollup = emptyRollup(monthKey);

  for (const txn of transactions) {
    if (txn.deleted || txn.monthKey !== monthKey) continue;
    applyDeltas(rollup, rollupDeltas(txn, accountType, 1));
  }

  return rollup;
}

/** Apply dot-path deltas to a rollup object, mirroring what `increment()` does server-side. */
export function applyDeltas(rollup: Rollup, fields: Record<string, number>): Rollup {
  for (const [path, value] of Object.entries(fields)) {
    const parts = path.split('.');

    if (parts.length === 1) {
      const key = parts[0] as keyof Rollup;
      (rollup[key] as number) = ((rollup[key] as number) ?? 0) + value;
      continue;
    }

    if (parts[0] === 'byCategory') {
      rollup.byCategory[parts[1]] = (rollup.byCategory[parts[1]] ?? 0) + value;
    } else if (parts[0] === 'byBusiness') {
      const key = parts[1] as 'business' | 'personal';
      rollup.byBusiness[key] = (rollup.byBusiness[key] ?? 0) + value;
    } else if (parts[0] === 'byAccount') {
      const flow = rollup.byAccount[parts[1]] ?? { inPaise: 0, outPaise: 0 };
      const side = parts[2] as keyof AccountFlow;
      flow[side] = (flow[side] ?? 0) + value;
      rollup.byAccount[parts[1]] = flow;
    }
  }
  return rollup;
}

/**
 * Compare two rollups, returning the paths that differ.
 *
 * Empty means the incremental and recomputed figures agree. The nightly job repairs on
 * any difference and logs that it did — silent drift is the failure mode that matters,
 * because a wrong dashboard figure looks exactly like a right one.
 */
export function diffRollups(a: Rollup, b: Rollup): string[] {
  const differences: string[] = [];

  // Absent and zero are the same thing here, and coalescing is not cosmetic. Zero-valued
  // deltas are deliberately never written, so a stored rollup has no key for a bucket
  // that nets to nothing while a recomputed one carries an explicit 0. Comparing those
  // strictly would report drift on almost every month and train the nightly repair to be
  // ignored.
  const check = (path: string, x: number | undefined, y: number | undefined) => {
    if ((x ?? 0) !== (y ?? 0)) differences.push(`${path}: ${x ?? 0} vs ${y ?? 0}`);
  };

  for (const key of [
    'incomePaise', 'expensePaise', 'savingsPaise', 'totalOutflowPaise',
    'transferPaise', 'lendingPaise', 'txnCount',
  ] as const) {
    check(key, a[key], b[key]);
  }

  for (const key of new Set([...Object.keys(a.byCategory), ...Object.keys(b.byCategory)])) {
    check(`byCategory.${key}`, a.byCategory[key] ?? 0, b.byCategory[key] ?? 0);
  }
  for (const key of ['business', 'personal'] as const) {
    check(`byBusiness.${key}`, a.byBusiness[key], b.byBusiness[key]);
  }
  for (const key of new Set([...Object.keys(a.byAccount), ...Object.keys(b.byAccount)])) {
    const x = a.byAccount[key] ?? { inPaise: 0, outPaise: 0 };
    const y = b.byAccount[key] ?? { inPaise: 0, outPaise: 0 };
    check(`byAccount.${key}.inPaise`, x.inPaise, y.inPaise);
    check(`byAccount.${key}.outPaise`, x.outPaise, y.outPaise);
  }

  return differences;
}

/** Sum several months, for a financial-year or calendar-year view. */
export function sumRollups(rollups: Rollup[], monthKey: MonthKey = ''): Rollup {
  const total = emptyRollup(monthKey);
  for (const rollup of rollups) {
    total.incomePaise += rollup.incomePaise;
    total.expensePaise += rollup.expensePaise;
    total.savingsPaise += rollup.savingsPaise;
    total.totalOutflowPaise += rollup.totalOutflowPaise;
    total.transferPaise += rollup.transferPaise;
    total.lendingPaise += rollup.lendingPaise;
    total.txnCount += rollup.txnCount;
    total.byBusiness.business += rollup.byBusiness.business;
    total.byBusiness.personal += rollup.byBusiness.personal;
    for (const [key, value] of Object.entries(rollup.byCategory)) {
      total.byCategory[key] = (total.byCategory[key] ?? 0) + value;
    }
    for (const [key, flow] of Object.entries(rollup.byAccount)) {
      const current = total.byAccount[key] ?? { inPaise: 0, outPaise: 0 };
      total.byAccount[key] = {
        inPaise: current.inPaise + flow.inPaise,
        outPaise: current.outPaise + flow.outPaise,
      };
    }
  }
  return total;
}
